import logger from 'ws-scripts/modules/logger';
import { addNewRelicPageAction } from 'ddc-new-relic';
import { publishEvent } from '../utilities/pubsub';
import {
	isValidPostalCode,
	parseRange,
	parseExcludeByRange
} from '../utilities/locationUtils';
import { getDefaultPostalCode } from '../utilities/defaultPostalCode';
import { getDDCPubSub } from '../utilities/ddcHelper';

// Configuration Constants
const DEFAULT_RANGE = 50;
const DEFAULT_EXCLUDE_BY_RANGE = true;
const WIDGET_NAME = 'ws-locations-data';
const TOPIC_LOCATIONS = 'locations';
const TOPIC_LOCATION_SELECTED = 'location-selected';

const LOG_PREFIX = `[${WIDGET_NAME}][LocationsData]`;

export class LocationsData {
	constructor(
		requestHandler,
		{ defaultPostalCode = getDefaultPostalCode() } = {}
	) {
		this.requestHandler = requestHandler;
		this.locations = [];
		this.postalCode = '';
		this.selectedLocationCoordinates = null;
		this.range = DEFAULT_RANGE;
		this.excludeByRange = DEFAULT_EXCLUDE_BY_RANGE;
		this.isDefaultView = false;
		this.defaultPostalCode = defaultPostalCode;
		this.error = null;
		this.TOPIC_PREFIX = WIDGET_NAME;
		this.LOCATIONS_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_LOCATIONS}`;
		this.LOCATION_SELECTED_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_LOCATION_SELECTED}`;
		logger.debug(`${LOG_PREFIX} Constructor called`);
	}

	// Register callback for location updates
	registerLocationsNotification(callback) {
		logger.debug(`${LOG_PREFIX} Registering subscriber`, {
			topic: this.LOCATIONS_TOPIC
		});

		this.subscribeEvent(this.LOCATIONS_TOPIC, callback);

		// Return unsubscribe function
		return () => {
			logger.debug(`${LOG_PREFIX} Unsubscribing`, {
				topic: this.LOCATIONS_TOPIC
			});
			this.unsubscribeEvent(this.LOCATIONS_TOPIC, callback);
		};
	}

	// Register callback for location selection events
	registerLocationSelectedNotification(callback) {
		logger.info(`${LOG_PREFIX} Registering location selected subscriber`, {
			topic: this.LOCATION_SELECTED_TOPIC
		});

		this.subscribeEvent(this.LOCATION_SELECTED_TOPIC, callback);

		// Return unsubscribe function
		return () => {
			logger.info(`${LOG_PREFIX} Unsubscribing from location selected`, {
				topic: this.LOCATION_SELECTED_TOPIC
			});
			this.unsubscribeEvent(this.LOCATION_SELECTED_TOPIC, callback);
		};
	}

	// Get all locations
	getLocations() {
		return this.locations;
	}

	// Get current selected location coordinates
	getSelectedLocationCoordinates() {
		return this.selectedLocationCoordinates;
	}

	// Get error message if any
	getError() {
		return this.error;
	}


	getPostalCode() {
		return this.postalCode || this.defaultPostalCode;
	}

	/*
	 * Set search parameters and trigger location fetch
	 * Validates postal code and only triggers fetch if parameters have changed
	 *
	 * @param {string} postalCode - User input postal code for location search
	 * @param {number} range - Search radius in miles (number or string)
	 * @param {boolean} excludeByRange - Whether to exclude locations beyond range (boolean or string)
	 * @param {boolean} isDefaultView - Whether this is the default view (uses default postal code and fixes number of tiles)
	 *
	 * @returns {void} Updates internal state and triggers location fetch if valid
	 */
	setSearchParams(postalCode, range, excludeByRange, isDefaultView = false) {
		let postalCodeToUse = postalCode;
		if (isDefaultView) {
			postalCodeToUse = this.defaultPostalCode;
		} else if (!isValidPostalCode(postalCodeToUse)) {
			logger.error(`${LOG_PREFIX} Invalid postal code:`, postalCodeToUse);
       
      // Track invalid postal code submission in New Relic
			addNewRelicPageAction('WS LOCATIONS DATA INVALID POSTAL CODE', {
				locationsPostalCode: postalCodeToUse,
				locationsRange: range
			});

			this.locations = [];
			this.error = 'Invalid postal code';
			publishEvent(this.LOCATIONS_TOPIC);
			return;
		}
		const trimmedPostalCode = postalCodeToUse.trim();
		const newRange = parseRange(range, this.range);
		const newExcludeByRange = parseExcludeByRange(
			excludeByRange,
			this.excludeByRange
		);

		const hasChanged =
			isDefaultView !== this.isDefaultView ||
			trimmedPostalCode !== this.postalCode ||
			newRange !== this.range ||
			newExcludeByRange !== this.excludeByRange;

		if (!hasChanged) {
			logger.debug(`${LOG_PREFIX} No changes detected, skipping fetch`);
			return;
		}
		this.isDefaultView = isDefaultView;
		this.postalCode = trimmedPostalCode;
		this.range = newRange;
		this.excludeByRange = newExcludeByRange;

		logger.debug(`${LOG_PREFIX} Parameters updated:`, {
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange
		});

		this._fetchLocations();
	}

	// Set location with coordinates
	setLocationWithCoordinates({ coordinates }) {
		logger.info(`${LOG_PREFIX} Setting location with coordinates`, {
			coordinates
		});

		// Store selected location coordinates
		this.selectedLocationCoordinates = coordinates;

		// Publish location selected event
		publishEvent(this.LOCATION_SELECTED_TOPIC, {
			coordinates
		});
	}

	// Trigger location fetch
	_fetchLocations() {
		logger.debug(`${LOG_PREFIX} Triggering fetch`, {
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange
		});

		this.requestHandler({
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange,
			isDefaultView: this.isDefaultView
		});
	}

	// eslint-disable-next-line class-methods-use-this
	subscribeEvent(topic, callback) {
		const pubsub = getDDCPubSub();
		if (pubsub) {
			logger.debug(`${LOG_PREFIX} Subscribing`, { topic });
			pubsub.subscribe(topic, callback);

			const subscribers = pubsub.subscribers?.[topic];
			logger.debug(`${LOG_PREFIX} Subscribed`, {
				topic,
				totalSubscribers: subscribers?.length || 0
			});
		} else {
			logger.warn(`${LOG_PREFIX} pubsub not available`, { topic });
		}
	}

	// Unsubscribe from event through PubSub
	// eslint-disable-next-line class-methods-use-this
	unsubscribeEvent(topic, callback) {
		const pubsub = getDDCPubSub();
		if (pubsub) {
			logger.debug(`${LOG_PREFIX} Unsubscribing`, { topic });
			pubsub.unsubscribe(topic, callback);

			const subscribers = pubsub.subscribers?.[topic];
			logger.debug(`${LOG_PREFIX} Unsubscribed`, {
				topic,
				totalSubscribers: subscribers?.length || 0
			});
		} else {
			logger.warn(`${LOG_PREFIX} pubsub not available for unsubscribe`, {
				topic
			});
		}
	}
}
